import { Button, Grid, Typography, alpha, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import { useLocation, useNavigate } from 'react-router';

import { Scrollbar, useResponsive } from '@zeta-core';
import { CheckCircleSmall, EditUnderline, KeyStraight, LogOutIcon, LoginTopIllustration, UserCircleIcon, ZetaLogo } from '@zeta-core/assets';
import { ReactNode } from 'react';
import { NAV } from '../config-layout';

type Props = {
  openNav: boolean;
};

interface IRegistrationSteps {
  stepIcon: ReactNode;
  stepActivatedIcon: ReactNode;
  stepName: string;
  stepActivated: boolean;
}

const RegistrationSteps = ({ stepIcon, stepActivatedIcon, stepName, stepActivated }: IRegistrationSteps) => {
  const theme = useTheme();

  return (
    <Grid container spacing={2}>
      <Grid
        size={{
          xs: 2.5,
          md: 2.5,
          lg: 2.5,
          xl: 2.5,
        }}
      >
        <Box display="flex" alignItems="center" height="100%" width="100%">
          <Stack
            sx={{
              background: stepActivated ? theme.palette.primary.main : theme.palette.grey[800],
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: 64,
              width: 48,
              height: 48,
            }}
          >
            {stepActivated ? stepActivatedIcon : stepIcon}
          </Stack>
        </Box>
      </Grid>
      <Grid
        size={{
          xs: 9.5,
          md: 9.5,
          lg: 9.5,
          xl: 9.5,
        }}
      >
        <Stack justifyContent="center" height="100%">
          <Typography variant="h6" color={stepActivated ? theme.palette.primary.contrastText : theme.palette.grey[600]}>
            {stepName}
          </Typography>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default function NavVertical({ openNav }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();

  const lgUp = useResponsive('up', 'lg');

  const { pathname } = useLocation();

  const steps = [
    {
      stepName: 'Terms & Conditions',
      stepActivatedIcon: <UserCircleIcon color={theme.palette.primary.contrastText} />,
      stepIcon: <UserCircleIcon color={theme.palette.grey[600]} />,
      stepPathnames: ['/signup/accounts/terms', '/signup/accounts/registration', '/signup/accounts/verification', '/signup/accounts/password-set-up'],
    },
    {
      stepName: 'Advisor Details',
      stepActivatedIcon: <UserCircleIcon color={theme.palette.primary.contrastText} />,
      stepIcon: <UserCircleIcon color={theme.palette.grey[600]} />,
      stepPathnames: ['/signup/accounts/advisor-details', '/signup/accounts/registration', '/signup/accounts/verification', '/signup/accounts/password-set-up'],
    },
    {
      stepName: 'Registration',
      stepActivatedIcon: <EditUnderline color={theme.palette.primary.contrastText} />,
      stepIcon: <EditUnderline color={theme.palette.grey[600]} />,
      stepPathnames: ['/signup/accounts/registration', '/signup/accounts/verification', '/signup/accounts/password-set-up'],
    },
    {
      stepName: 'Verification',
      stepActivatedIcon: <CheckCircleSmall color={theme.palette.primary.contrastText} />,
      stepIcon: <CheckCircleSmall color={theme.palette.grey[600]} />,
      stepPathnames: ['/signup/accounts/verification', '/signup/accounts/password-set-up'],
    },
    {
      stepName: 'Username & Password',
      stepActivatedIcon: <KeyStraight color={theme.palette.primary.contrastText} />,
      stepIcon: <KeyStraight color={theme.palette.grey[600]} />,
      stepPathnames: ['/signup/accounts/password-set-up'],
    },
  ];

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack
        p={7}
        height="100vh"
        sx={{
          background: theme.palette.grey[900],
          overflowY: 'auto',
        }}
        justifyContent="space-between"
        direction="column"
      >
        <Box
          sx={{
            position: 'absolute',
            right: '-55px',
            top: '-65px',
            zIndex: 1000,
            transform: 'rotate(90deg)',
          }}
        >
          <LoginTopIllustration />
        </Box>

        <Stack gap={5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <ZetaLogo />
            <Box onClick={() => navigate('/auth/login')} sx={{ cursor: 'pointer', zIndex: 10000 }}>
              {' '}
              <LogOutIcon />{' '}
            </Box>
          </Stack>

          <Stack mt={5}>
            <Typography variant="h3" color="background.default">
              Onboarding
            </Typography>
            <Typography variant="body2" color={`${theme.palette.grey[500]}`}>
              Please complete the below sections to onboard with Zeta.
            </Typography>
          </Stack>

          {steps.map((step, index) => (
            <Stack key={step.stepName} sx={{ position: 'relative' }}>
              <RegistrationSteps
                key={step.stepName}
                stepIcon={step.stepIcon}
                stepActivatedIcon={step.stepActivatedIcon}
                stepName={step.stepName}
                stepActivated={step.stepPathnames.some((path) => pathname.includes(path))}
              />

              {index !== steps.length - 1 ? (
                <Stack
                  sx={{
                    width: 12,
                    height: 40,
                    position: 'absolute',
                    top: '3rem',
                    left: '1rem',
                  }}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: '3px',
                      bgcolor: steps[index + 1].stepPathnames.some((path) => pathname.includes(path))
                        ? theme.palette.primary.main
                        : alpha(theme.palette.divider, 0.2),
                    }}
                  />
                </Stack>
              ) : null}
            </Stack>
          ))}
        </Stack>

        <Box
          sx={{
            position: 'absolute',
            left: '-75px',
            bottom: '60px',
            zIndex: 1000,
            transform: 'rotate(-90deg)',
          }}
        >
          <LoginTopIllustration />
        </Box>

        <Stack alignItems="center" justifyContent="center" direction="row">
          <Typography variant="body1" color="text.disabled">
            Already have an account?
          </Typography>
          <Button variant="text" color="primary" size="medium" onClick={() => navigate('/auth/login')}>
            Login
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_VERTICAL_ONBOARDING },
      }}
    >
      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.W_VERTICAL_ONBOARDING,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          slotProps={{
            paper: {
              sx: {
                width: NAV.W_VERTICAL_ONBOARDING,
              },
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
